<template>
    <tbody
        class="ql-structure-header"
        :class="'ql-structure-header--' + resolvedType"
        :data-ql-structure="markerKey"
    >
        <tr>
            <td colspan="100">
                <div
                    class="ql-structure-header-inner"
                    :style="barStyle"
                    :title="jumpTooltip"
                    @click="switchViewToPage"
                >
                    <!-- Same ♪ character as the on-page marker, and for the same
                         reason: the type belongs on the row, not inside the
                         label text. Kept OUT of `labelText`, which also feeds
                         the CSV export. -->
                    <span
                        v-if="resolvedType === 'song'"
                        class="ql-structure-header-glyph"
                        aria-label="Song"
                    >&#9834;</span>
                    <span class="ql-structure-header-label">{{ labelText }}</span>
                    <span class="ql-structure-header-meta">
                        <span v-if="empty" class="ql-structure-header-nocues">no cues</span>p {{ pageName }}
                    </span>
                </div>
            </td>
        </tr>
    </tbody>
</template>

<script>
import { mapGetters } from 'vuex';

import { sidebarMarkerRowText } from '@/lib/marker-labels';
import { MARKER_TYPE_COLORS, pdfPageLabel } from '@/lib/structureOrder';

/**
 * One Act / Scene / Song section header inside the cue list.
 *
 * Deliberately carries NO `data-ql-r` / `data-ql-c` attributes. Everything in the
 * spreadsheet layer resolves cells through `[data-ql-r][data-ql-c]`, so arrow
 * navigation, range painting, copy and paste all skip this row by construction
 * rather than by special case — see listViewCellNav.js.
 *
 * Display-only: markers are authored on the PDF and in Manage Structure.
 */
const StructureHeaderRow = {
    name: 'ProjectListViewStructureHeaderRow',
    props: {
        marker: {
            type: Object,
            required: true
        },
        // No cue of its own: either its cues are filtered out, or it sits past
        // the last cue in the project. Labelled rather than hidden.
        empty: {
            type: Boolean,
            default: false
        },
        splitView: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        ...mapGetters([
            'project'
        ]),
        resolvedType() {
            const type = this.marker && this.marker.type;
            if (type === 'act' || type === 'scene' || type === 'song') {
                return type;
            }
            return 'scene';
        },
        markerKey() {
            return String((this.marker && (this.marker.markerId || this.marker.id)) || '');
        },
        // Same rule as the sidebar: songs drop the literal "Song" and gain quotes.
        labelText() {
            return sidebarMarkerRowText(this.marker);
        },
        pageName() {
            const pages = this.project ? this.project.pdfPageNumbers : [];
            return pdfPageLabel(pages, this.marker && this.marker.pageIndex);
        },
        // The same click does two different things, so say which one. In split
        // view the PDF pane moves and the list stays; full-screen, the list is
        // closed to show the page — a whole-view change worth warning about.
        jumpTooltip() {
            return this.splitView ? 'Jump the PDF to this page' : 'Leave the list and open this page';
        },
        // Inline so the bar cannot drift from the on-page mark, which reads the
        // same palette out of structureOrder.js.
        barStyle() {
            const swatch = MARKER_TYPE_COLORS[this.resolvedType] || MARKER_TYPE_COLORS.scene;
            return {
                backgroundColor: swatch.bg,
                color: swatch.fg
            };
        }
    },
    methods: {
        // Same path the page-number cell uses: list-view forwards this up to
        // views/project.vue, which moves the PDF page in split view and leaves
        // the full-screen list first when not in split.
        //
        // Number() matters — pageIndex can arrive as a string, and the page
        // getters compare with strict equality.
        switchViewToPage: function() {
            this.$emit('switchViewToPage', Number(this.marker && this.marker.pageIndex));
        }
    }
};

export default StructureHeaderRow;
</script>

<style lang="scss">
/* Fixed height: STRUCTURE_ROW_H in list-view.vue must match, or the virtualizer's
   offsets drift. Single line + nowrap, so there is nothing to wrap. */
$ql-structure-row-h: 28px;

.ql-structure-header {
    > tr > td {
        /* Beat the table's global `tbody td` border/padding/max-width rules so the
           coloured bar reads as one unbroken band. */
        border: 0;
        padding: 0;
        max-width: none;
        overflow: visible;
        height: $ql-structure-row-h;
    }

    /* NOT position:sticky vertically. A second sticky layer under the sticky
       <thead>, inside a fixed-layout table that also has sticky-left columns in
       split view, is what forced the colgroup revert (911485f5 -> eb4859b5).
       Only the inner div pins horizontally, so the label survives an X scroll. */
    .ql-structure-header-inner {
        position: sticky;
        left: 0;
        cursor: pointer;

        &:hover {
            filter: brightness(1.08);
        }
        display: flex;
        align-items: center;
        gap: 6px;
        height: $ql-structure-row-h;
        padding: 0 8px;
        font-size: 11px;
        line-height: $ql-structure-row-h;
        white-space: nowrap;
        overflow: hidden;
    }

    /* Song note, matching the on-page marker. em-sized so it follows the row's
       font at every breakpoint below; the parent already supplies the gap. */
    .ql-structure-header-glyph {
        flex: none;
        font-size: 1.1em;
        line-height: 1;
        opacity: .85;
        user-select: none;
    }

    .ql-structure-header-label {
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .ql-structure-header-meta {
        margin-left: auto;
        padding-left: 12px;
        font-weight: 400;
        font-style: normal;
        text-transform: none;
        letter-spacing: 0;
        opacity: 0.75;
        flex: 0 0 auto;
    }

    /* A section with no cues is deliberate, not a rendering fault — say so. */
    .ql-structure-header-nocues {
        margin-right: 8px;

        &::after {
            content: '·';
            margin-left: 8px;
        }
    }
}

/* Act reads as the major division: heaviest, uppercase, and ruled off above. */
.ql-structure-header--act {
    > tr > td {
        border-top: 2px solid rgba(0, 0, 0, 0.25);
    }

    .ql-structure-header-label {
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }
}

.ql-structure-header--scene {
    .ql-structure-header-inner {
        padding-left: 24px;
    }

    .ql-structure-header-label {
        font-weight: 600;
    }
}

.ql-structure-header--song {
    .ql-structure-header-inner {
        padding-left: 24px;
    }

    .ql-structure-header-label {
        font-weight: 500;
        font-style: italic;
        /* Italic glyphs lean past their layout box on the right, and the label is
           `overflow: hidden` for long titles — which sheared the closing quote off
           every song ("'Poor me" instead of "'Poor me'"). Give the overhang room.
           Song-scoped: act and scene are upright and have nothing to overhang. */
        padding-right: 0.25em;
    }
}

/* Dark mode: the three type colours HOLD, matching the on-page marks and the
   Manage structure dialog's badges. Staging flattens markers to #424242 in dark
   mode; a staging merge will try to bring that back here — it should not.
   See point 3 of docs/STRUCTURE_PORT_HANDOFF.md. */
.md-theme-show-mode {
    .ql-structure-header--act > tr > td {
        border-top-color: rgba(255, 255, 255, 0.25);
    }
}
</style>


// WEBPACK FOOTER //
// structure-header-row.vue?5b7ca5e0